import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import Button from "./Button";
import "./Modal.css";

interface Props {
  title: React.ReactNode;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "small" | "medium" | "large";
  role?: "dialog" | "alertdialog";
  dismissible?: boolean;
}
let scrollLocks = 0;
let previousOverflow = "";
export default function Modal({ title, description, onClose, children, footer, size = "large", role = "dialog", dismissible = true }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const element = ref.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    if (!element) return;
    if (!scrollLocks++) { previousOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; }
    if (typeof element.showModal === "function") element.showModal(); else element.setAttribute("open", "");
    (element.querySelector<HTMLElement>('[autofocus], [data-autofocus="true"]') || element.querySelector<HTMLElement>('button:not(:disabled), input:not(:disabled)') || element).focus();
    return () => {
      if (element.open && typeof element.close === "function") element.close();
      if (!--scrollLocks) document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  return createPortal(<dialog ref={ref} role={role} aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-description` : undefined}
    tabIndex={-1} className={`navo-dialog navo-dialog--${size}`}
    onKeyDown={event => { if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); if (dismissible) onClose(); } }}
    onCancel={event => { event.preventDefault(); if (dismissible) onClose(); }}
    onClick={event => {
      if (!dismissible || event.target !== event.currentTarget) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
    }}>
    <header className="navo-dialog__header"><div><h2 id={`${id}-title`}>{title}</h2>{description && <p id={`${id}-description`}>{description}</p>}</div>
      <Button className="navo-dialog__close" variant="text" aria-label="Close dialog" onClick={onClose}><FiX aria-hidden="true" /></Button>
    </header>
    <div className="navo-dialog__body">{children}</div>
    {footer && <footer className="navo-dialog__footer">{footer}</footer>}
  </dialog>, document.body);
}
